import React, { useEffect, useState } from 'react';
import { gql } from 'apollo-boost';
import { useApolloClient, useMutation } from '@apollo/react-hooks';

import Modal from '../Modal/Modal';
import VerifyOtp from '../Session/VerifyOtp';
import TextInput from '../Form/TextInput/TextInput';
import Button from '../Form/Button/Button';
import successImg from '../../assets/images/success.png';
import { useLogger } from '../../contexts/Logger';
import { useUnifiedAnalytics } from '../../contexts/AnalyticsWrapper';
import { useCurrentUser } from '../../contexts/CurrentUserProvider';
import { GET_CUSTOMER_AND_ACCOUNTS as GET_MANAGE_ACCOUNT_CUSTOMER_AND_ACCOUNTS } from '../../contexts/CustomerDataProvider';
import { GET_CUSTOMER_AND_ACCOUNTS as GET_DASHBOARD_CUSTOMER_AND_ACCOUNTS } from '../DashboardNew/queries/customerAndAccountsQuery';

const SEND_OTP = gql`
    mutation SendLoggedInOtp($request : SendOtpRequestType!){
        sendLoggedInOtp(request:$request) {
            success
            verificationToken
        }
    }
`;

const VERIFY_OTP = gql`
    mutation VerifyOtp($request : VerifyOtpRequestType!){
        verifyOtp(request:$request) {
            success
            email
            multipleEmails
            completionToken
        }
    }
`;

const CANCEL_TRANSFER_OUT = gql`
    mutation CancelTransferOut($request: CancelTransferOutRequestType!) {
        cancelTransferOut(request: $request) {
            success
        }
    }
`;

/**
 * @param {{
 *   accountId: string;
 *   mobileNumber: string;
 *   productKey?: string;
 *   onCancel: () => void;
 *   onComplete: () => void;
 *   onCancellationRequested?: (updatedAt: string) => void;
 * }} props
 */
function TransferOutCancellationModal({
    accountId,
    mobileNumber,
    productKey = 'fisa',
    onCancel,
    onComplete,
    onCancellationRequested
}) {
    const client = useApolloClient();
    const currentUser = useCurrentUser();
    const logger = useLogger();
    const analytics = useUnifiedAnalytics();
    const [step, setStep] = useState('otp');
    const [verificationToken, setVerificationToken] = useState('');
    const [sendOtpFailed, setSendOtpFailed] = useState(false);

    const [sendOtp] = useMutation(SEND_OTP);
    const [verifyOtp] = useMutation(VERIFY_OTP);
    const [cancelTransferOut] = useMutation(CANCEL_TRANSFER_OUT);

    useEffect(() => {
        void requestOtp();
    }, []);

    /**
     * @param {string} updatedAt
     */
    const updateTransferOutStatusInCache = (updatedAt) => {
        if (!currentUser?.customerId) {
            return;
        }

        const variables = { id: currentUser.customerId };
        const queries = [
            GET_MANAGE_ACCOUNT_CUSTOMER_AND_ACCOUNTS,
            GET_DASHBOARD_CUSTOMER_AND_ACCOUNTS
        ];

        queries.forEach((query) => {
            try {
                /** @type {{ customer?: { accounts?: Array<any> } } | null} */
                const cachedData = client.readQuery({
                    query,
                    variables
                });

                if (!cachedData?.customer?.accounts) {
                    return;
                }

                /** @type {Array<{ id?: string; transferOut?: { status?: string; lastUpdatedAt?: string } }>} */
                const cachedAccounts = cachedData.customer.accounts;

                client.writeQuery({
                    query,
                    variables,
                    data: {
                        ...cachedData,
                        customer: {
                            ...cachedData.customer,
                            accounts: cachedAccounts.map((account) => {
                                if (account.id !== accountId) {
                                    return account;
                                }

                                return {
                                    ...account,
                                    transferOut: {
                                        ...(account.transferOut || {}),
                                        status: 'CancellationRequested',
                                        lastUpdatedAt: updatedAt
                                    }
                                };
                            })
                        }
                    }
                });
            } catch (_e) {
                // Query may not be present in cache for the current journey.
            }
        });
    };

    const requestOtp = async () => {
        try {
            const result = await sendOtp({
                variables: {
                    request: {}
                }
            });

            /** @type {{ sendLoggedInOtp?: { verificationToken?: string } } | undefined} */
            const data = result?.data;
            const token = data?.sendLoggedInOtp?.verificationToken;

            if (!token) {
                throw new Error('Missing verification token for transfer out cancellation');
            }

            setVerificationToken(token);
            // Track OTP requested via CADI
            try {
                analytics.trackAnalyticsOtpEvent(
                    'request one-time passcode',
                    'account details',
                    'request',
                    productKey,
                    'otp|request|transfer_out_cancellation'
                );
            } catch (_e) {
                // ignore analytics errors
            }
            setSendOtpFailed(false);
        } catch (e) {
            logger.error(
                '[TransferOutCancellationModal] Failed to send OTP for transfer out cancellation',
                e instanceof Error ? e : new Error(String(e))
            );
            try {
                analytics.trackAnalyticsOtpEvent(
                    'request one-time passcode failed',
                    'account details',
                    'failure',
                    productKey,
                    'otp|request_failed|transfer_out_cancellation'
                );
            } catch (_e) {
                // ignore analytics errors
            }
            setSendOtpFailed(true);
        }
    };

    /** @type {import('../Session/VerifyOtpTypes').CustomVerify} */
    const customVerification = async (otp, mode) => {
        // Track OTP verification attempt
        try {
            analytics.trackAnalyticsOtpEvent(
                'verify one-time passcode',
                'account details',
                'verify',
                productKey,
                'otp|verify|transfer_out_cancellation'
            );
        } catch (_e) {
            // ignore analytics errors
        }
        try {
            const verifyResult = await verifyOtp({
                variables: {
                    request: {
                        passCode: otp,
                        verificationToken,
                        mode
                    }
                }
            });

            /** @type {{ verifyOtp?: { success?: boolean; completionToken?: string } } | undefined} */
            const verifyData = verifyResult?.data;
            const verifyResponse = verifyData?.verifyOtp;

            if (!verifyResponse?.success || !verifyResponse?.completionToken) {
                return {
                    success: false,
                    data: {
                        success: false,
                        email: ''
                    }
                };
            }

            const cancelResult = await cancelTransferOut({
                variables: {
                    request: {
                        accountId,
                        completionToken: verifyResponse.completionToken
                    }
                }
            });

            /** @type {{ cancelTransferOut?: { success?: boolean } } | undefined} */
            const cancelData = cancelResult?.data;

            if (!cancelData?.cancelTransferOut?.success) {
                try {
                    analytics.trackAnalyticsOtpEvent(
                        'transfer out cancellation request failed',
                        'account details',
                        'failure',
                        productKey,
                        'otp|cancellation_request_failed|transfer_out'
                    );
                } catch (_e) {
                    // ignore analytics errors
                }
                return {
                    success: false,
                    data: {
                        success: false,
                        email: ''
                    },
                    message: ['UnexpectedError']
                };
            }

            const updatedAt = new Date().toISOString();
            updateTransferOutStatusInCache(updatedAt);

            // Track successful cancellation request
            try {
                analytics.trackAnalyticsOtpEvent(
                    'transfer out cancellation requested',
                    'account details',
                    'cancellation_requested',
                    productKey,
                    'otp|cancellation_requested|transfer_out'
                );
            } catch (_e) {
                // ignore analytics errors
            }

            if (onCancellationRequested) {
                onCancellationRequested(updatedAt);
            }
            setStep('success');

            return {
                success: true,
                data: {
                    success: true,
                    email: '',
                    completionToken: verifyResponse.completionToken
                }
            };
        } catch (e) {
            logger.error(
                '[TransferOutCancellationModal] Failed to verify OTP or request transfer out cancellation',
                e instanceof Error ? e : new Error(String(e))
            );

            try {
                analytics.trackAnalyticsOtpEvent(
                    'verify one-time passcode failed',
                    'account details',
                    'failure',
                    productKey,
                    'otp|verify_failed|transfer_out_cancellation'
                );
            } catch (_e) {
                // ignore analytics errors
            }

            return {
                success: false,
                data: {
                    success: false,
                    email: ''
                },
                message: ['UnexpectedError']
            };
        }
    };

    const renderWaitingState = () => (
        <>
            <h2 className="modal__title">Verify transfer cancellation</h2>
            <div className="login__line">
                Please enter the one-time passcode we texted you.
            </div>
            <label>One-time passcode</label>
            <TextInput
                className="login__input"
                value=""
                onChange={() => {}}
                type="text"
                disabled
            />
            <Button
                customClassNames={['loginButton', 'button--primary']}
                disabled
                label="Next"
            />
        </>
    );

    const renderSendOtpError = () => (
        <>
            <h2 className="modal__title">Verify transfer cancellation</h2>
            <div className="login__error">
                <span className="login__error--wrap">
                    We couldn&apos;t send a one-time passcode right now. Please try again later.
                </span>
            </div>
            <Button size='med' customClassNames={['modal__button']} label="OK" onClick={onCancel} />
        </>
    );

    const renderOtpStep = () => {
        if (sendOtpFailed) {
            return renderSendOtpError();
        }

        if (!verificationToken) {
            return renderWaitingState();
        }

        return (
            <>
                <h2 className="modal__title">Verify transfer cancellation</h2>
                <VerifyOtp
                    verificationToken={verificationToken}
                    mobileNumber={mobileNumber}
                    customVerify={customVerification}
                    onSuccess={() => {}}
                    analyticsRegion="account details"
                    analyticsAction="transfer out cancellation"
                />
            </>
        );
    };

    const renderSuccessStep = () => (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            <div className="modal__spacer--small" />
            <img src={successImg} className='account-activity__image' alt="Success" />
            <h4 className="modal__title-closure">Thank you</h4>
            <p className="modal__text-closure" style={{ maxWidth: '360px', margin: '0.5rem auto', lineHeight: 1.2, fontSize: '0.95rem' }}>
                We&apos;ve asked for this transfer to be cancelled. We&apos;ll let you know once it&apos;s done.
            </p>

            <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                <div style={{ marginRight: '0.25rem' }}>
                    <Button size='med' customClassNames={['modal__button']} label="OK" onClick={onComplete} />
                </div>
            </div>
        </div>
    );

    return (
        <Modal
            className="modal modal--fixed-height inactivity-modal modal__mobile-optimised"
            onClose={step === 'success' ? onComplete : onCancel}
            mobileOptimised
        >
            {step === 'success' ? renderSuccessStep() : renderOtpStep()}
        </Modal>
    );
}

export default TransferOutCancellationModal;
